import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { axiosInstance } from "../App";
import { Button } from "@/components/ui/button";
import {
  Database,
  LogOut,
  BarChart3,
  ShieldCheck,
  Globe,
  Shield,
  UserPlus,
  ClipboardList,
  CheckSquare,
  FileText,
  Activity,
  TrendingUp,
  Download,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const MENU_GROUPS = [
  {
    label: "Dashboard",
    items: [
      { key: null, label: "Dashboard", path: "/dashboard", icon: Database },
      { key: "analytics", label: "Analytics Dashboard", path: "/analytics", icon: BarChart3 },
    ],
  },
  {
    label: "Member",
    items: [
      { key: "input_member", label: "Input Member", path: "/marketing", icon: UserPlus },
      { key: "follow_up_panel", label: "Follow Up Member", path: "/follow-up", icon: ClipboardList },
      { key: "todo_list", label: "Submit Member", path: "/todo-list", icon: CheckSquare },
      { key: "member_report", label: "Member Report", path: "/member-report", icon: FileText },
    ],
  },
  {
    label: "Performance",
    items: [
      { key: "marketing_perf", label: "Performance Report", path: "/marketing-performance", icon: TrendingUp },
      { key: "performance_review", label: "Performance Review", path: "/performance-report", icon: Activity },
    ],
  },
  {
    label: "Log",
    items: [
      { key: "export_logs", label: "Log Export Download", path: "/export-logs", icon: Download },
      { key: "member_history", label: "Member History Log", path: "/member-history", icon: FileText },
    ],
  },
  {
    label: "Management",
    items: [
      { key: "user_mgmt_users", label: "User Management", path: "/superadmin?tab=users", icon: ShieldCheck },
      { key: "user_mgmt_websites", label: "Website Management", path: "/superadmin?tab=website", icon: Globe },
      { key: "user_mgmt_roles", label: "Role Management", path: "/superadmin?tab=roles", icon: Shield },
    ],
  },
];

const getRoleDisplay = (role) => {
  const roleMap = {
    superadmin: "Super Admin",
    marketing: "Marketing",
    follow_up: "Follow Up",
    supervisor: "Supervisor",
  };
  return roleMap[role] || role;
};

const SidebarContent = ({ user, onItemClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [allowedMenus, setAllowedMenus] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get("/menus/me")
      .then((resp) => {
        if (!cancelled) setAllowedMenus(resp.data.menus || []);
      })
      .catch(() => {
        if (!cancelled) setAllowedMenus([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
    toast.success("Logout berhasil");
  };

  const isItemVisible = (item) => {
    if (item.key === null) return true;
    if (allowedMenus === null) return false;
    return allowedMenus.includes(item.key);
  };

  const matchItem = (item) => {
    const [path, search = ""] = item.path.split("?");
    const currentSearch = location.search.startsWith("?") ? location.search.slice(1) : location.search;
    if (location.pathname !== path) return false;
    if (!search) return currentSearch === "";
    return currentSearch === search;
  };

  const visibleGroups = MENU_GROUPS
    .map((g) => ({ ...g, items: g.items.filter(isItemVisible) }))
    .filter((g) => g.items.length > 0);

  const isGroupOpen = (group) => {
    if (group.items.some(matchItem)) return true;
    return !!expandedGroups[group.label];
  };

  const toggleGroup = (label) => {
    setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <div className="flex h-full flex-col">
      <div className="px-6 py-6 border-b border-red-900/30">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-red-800 rounded-xl flex items-center justify-center shadow-lg shadow-red-900/30">
            <Database className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-xl font-bold text-red-50">Marketing Hub</h1>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {visibleGroups.map((group) => {
          const open = isGroupOpen(group);
          return (
            <div key={group.label}>
              <button
                onClick={() => toggleGroup(group.label)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider text-gray-400 hover:text-white hover:bg-red-900/10 transition-colors"
                data-testid={`sidebar-group-${group.label.toLowerCase()}`}
              >
                <span>{group.label}</span>
                {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
              {open && (
                <div className="mt-1 ml-2 space-y-1 border-l border-red-900/30 pl-2">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = matchItem(item);
                    return (
                      <button
                        key={item.path}
                        onClick={() => {
                          navigate(item.path);
                          onItemClick?.();
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isActive
                            ? "bg-red-600/20 text-red-300 border border-red-700/40"
                            : "text-gray-300 hover:text-white hover:bg-red-900/20"
                        }`}
                        data-testid={`sidebar-${item.path.replace(/[/?=]/g, "-")}`}
                      >
                        {Icon && <Icon size={16} />}
                        <span className="truncate">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="border-t border-red-900/30 px-3 py-4 space-y-2">
        <div className="flex items-center gap-2 bg-[#0f0f10] px-3 py-2 rounded-lg border border-red-900/30">
          <div className="w-9 h-9 bg-gradient-to-br from-red-600 to-red-800 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          <div className="text-sm min-w-0">
            <div className="font-medium text-white truncate">{user?.name}</div>
            <div className="text-xs text-gray-400 truncate">{getRoleDisplay(user?.role)}</div>
          </div>
        </div>
        <Button
          onClick={handleLogout}
          variant="ghost"
          className="w-full justify-start text-gray-300 hover:text-white hover:bg-red-900/20"
          data-testid="logout-button"
        >
          <LogOut size={18} className="mr-2" />
          Logout
        </Button>
      </div>
    </div>
  );
};

/**
 * Sidebar: fixed desktop + mobile-drawer wrapper.
 * Use via <AppLayout> on every protected page so the menu persists.
 */
const Sidebar = ({ user, mobileOpen, onMobileClose }) => {
  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-[#1a1a1b]/95 backdrop-blur-xl border-r border-red-900/30 z-30">
        <SidebarContent user={user} />
      </aside>

      {/* Mobile Sidebar (drawer) */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/60"
          onClick={onMobileClose}
          data-testid="sidebar-mobile-overlay"
        >
          <div
            className="absolute left-0 top-0 h-full w-72 bg-[#1a1a1b] border-r border-red-900/30"
            onClick={(e) => e.stopPropagation()}
          >
            <SidebarContent user={user} onItemClick={onMobileClose} />
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
